import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { TestSublease } from '../../types/test-sublease';
import { useClickOutside } from '../../hooks/useClickOutside';
import { supabase } from '../../lib/supabase';
import { supabaseLotService } from '../../services/supabase/lots'; // Import lot service
import { format, parse } from 'date-fns';

interface EditTestSubLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sublease: TestSublease;
  preventCloseOnOutsideClick?: boolean;
}

export default function EditTestSubLeaseModal({
  isOpen,
  onClose,
  onSuccess,
  sublease,
  preventCloseOnOutsideClick = true
}: EditTestSubLeaseModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);

  // Valid rental frequency options
  const rentalFrequencyOptions = [
    "Monthly",
    "Quarterly",
    "Annually",
    "When requested"
  ];

  // Valid lease method options with descriptions
  const leaseMethodOptions = [
    { code: null, description: "None" },
    { code: "C", description: "Non-Retail Commercial (Non-Government)" },
    { code: "CG", description: "Non-Retail Commercial (Government)" },
    { code: "CH", description: "Community housing (peppercorn)" },
    { code: "PS", description: "Public Space" },
    { code: "R3", description: "Retail - (Commercial) (Under 3 million)" },
    { code: "R5", description: "Retail - (Commercial) (Gross income over 5 mil)" },
    { code: "RG", description: "Residential (Government)" },
    { code: "RN", description: "Residential (Non-Government)" },
    { code: "SD", description: "Short-term Development Sublease" },
    { code: "U", description: "Undeveloped (large parcels)" }
  ];

  const [formData, setFormData] = useState<TestSublease>({...sublease});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaseholders, setLeaseholders] = useState<{id: string, name: string}[]>([]);
  
  // State for lot selection
  const [selectedLots, setSelectedLots] = useState<string[]>([]);
  const [initiallyAssociatedLotIds, setInitiallyAssociatedLotIds] = useState<string[]>([]);
  const [currentAssociationDetails, setCurrentAssociationDetails] = useState<Array<{lot_id: string, lot_desc: string | null, community_name?: string | null}>>([]);
  const [selectableLotPool, setSelectableLotPool] = useState<Array<{lot_id: string, lot_desc: string | null, community_name?: string | null}>>([]);
  const [leaseLotUse, setLeaseLotUse] = useState<string>('');

  // Helper to enhance lot data with community names
  const enhanceLotsWithCommunityNames = async (lotsData: any[]) => {
    const communityIds = lotsData
      .map(lot => lot.lot_community)
      .filter((id): id is string => id !== null && id !== undefined);

    let communityMap: Record<string, string> = {};
    if (communityIds.length > 0) {
      try {
        const { data: communities, error: commError } = await supabase
          .from('communities')
          .select('comm_id, comm_name')
          .in('comm_id', communityIds);
        if (!commError && communities) {
          communityMap = communities.reduce((map, comm) => {
            map[comm.comm_id] = comm.comm_name;
            return map;
          }, {} as Record<string, string>);
        } else if (commError) console.warn('Could not fetch community names:', commError);
      } catch (commFetchError) {
        console.error('Error fetching communities:', commFetchError);
      }
    }
    return lotsData.map(lot => ({
      ...lot, // Spread existing lot properties
      lot_id: lot.lot_id, // Ensure lot_id is present
      lot_desc: lot.lot_desc || 'No description',
      community_name: lot.lot_community ? communityMap[lot.lot_community] || `Community ${lot.lot_community}` : 'Unknown'
    }));
  };
  
  // Function to fetch existing associated lots (IDs and use)
  const fetchInitialAssociations = async (leaseId: string) => {
    try {
      const { data, error } = await supabase
        .from('lease_lots')
        .select('leaselot_lot, leaselot_use')
        .eq('leaselot_lease', leaseId);

      if (error) throw error;

      if (data && data.length > 0) {
        const lotIds = data.map(item => item.leaselot_lot);
        setSelectedLots(lotIds); // Pre-select currently associated lots
        setInitiallyAssociatedLotIds(lotIds);
        if (data[0].leaselot_use) setLeaseLotUse(data[0].leaselot_use);
        console.log(`Found ${lotIds.length} initially associated lot IDs:`, lotIds);
        return lotIds; // Return IDs for fetching details
      } else {
        console.log('No lots initially associated with this sublease');
        setSelectedLots([]);
        setInitiallyAssociatedLotIds([]);
        setLeaseLotUse('');
        return [];
      }
    } catch (err) {
      console.error('Error fetching initial lease lots:', err);
      setError('Failed to load initial lot associations');
      return [];
    }
  };

  // Handle checkbox change for lot selection
  const handleLotCheckboxChange = (lotId: string) => {
    setSelectedLots(prev => {
      if (prev.includes(lotId)) {
        return prev.filter(id => id !== lotId);
      } else {
        return [...prev, lotId];
      }
    });
  };

  // Handle leaseLotUse change
  const handleLeaseLotUseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLeaseLotUse(e.target.value);
  };

  // Fetch leaseholders and lots for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaseholders
        const { data, error } = await supabase
          .from('leaseholders')
          .select('holder_id, holder_name')
          .order('holder_name', { ascending: true });
        
        if (error) throw error;
        
        setLeaseholders(data.map(item => ({
          id: item.holder_id,
          name: item.holder_name
        })));

        // Fetch leaseholders
        const { data: leaseholderData, error: leaseholderError } = await supabase
          .from('leaseholders')
          .select('holder_id, holder_name')
          .order('holder_name', { ascending: true });
        
        if (leaseholderError) throw leaseholderError;
        
        setLeaseholders(leaseholderData.map(item => ({
          id: item.holder_id,
          name: item.holder_name
        })));

        // Fetch initial lot associations (IDs only first)
        let associatedIds: string[] = [];
        if (sublease && sublease.lease_id) {
          associatedIds = await fetchInitialAssociations(sublease.lease_id);
        }

        // Fetch full details for initially associated lots
        if (associatedIds.length > 0) {
          const details = await supabaseLotService.getLotsByIds(associatedIds);
          const enhancedDetails = await enhanceLotsWithCommunityNames(details);
          setCurrentAssociationDetails(enhancedDetails);
        } else {
          setCurrentAssociationDetails([]);
        }
        
        // Fetch all selectable lots (lot_avail = 'Y')
        const selectableLotsData = await supabaseLotService.getSelectableLotsForAssociation();
        const enhancedSelectableLots = await enhanceLotsWithCommunityNames(selectableLotsData);
        setSelectableLotPool(enhancedSelectableLots);

      } catch (err) {
        console.error('Error fetching modal data:', err);
        setError('Failed to load form data');
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, sublease]);

  // Format dates for form inputs
  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      // Parse the date string and format it as YYYY-MM-DD for input[type="date"]
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for the lease_type field to ensure consistent formatting
    if (name === 'lease_type') {
      // Store the value as-is, but we'll handle it properly during submission
      setFormData(prev => ({ ...prev, [name]: value }));
      console.log(`Setting lease_type to: "${value}", length: ${value.length}, type: ${typeof value}`);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Optional validation for lots
    if (selectedLots.length === 0) {
      setError('Please select at least one lot to associate with this sublease');
      setIsSubmitting(false);
      return;
    }

    // Create a timeout promise to prevent hanging operations
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out after 10 seconds')), 10000);
    });

    try {
      console.log('Updating sublease with ID:', formData.lease_id);
      
      // Create the update data object with proper string handling for lease_type
      const updateData = {
        lease_holder: formData.lease_holder,
        lease_start: formData.lease_start,
        lease_end: formData.lease_end,
        lease_method: formData.lease_method,
        // Ensure proper handling of lease_type string value
        lease_type: formData.lease_type?.trim() || null, // Convert empty strings to null
        lease_furth_term: formData.lease_furth_term,
        lease_rent_freq: formData.lease_rent_freq,
        lease_dealing: formData.lease_dealing,
        lease_deal_doc: formData.lease_deal_doc
      };
      
      console.log('Update data:', updateData);
      console.log('Lease type value:', {
        rawValue: formData.lease_type,
        processedValue: updateData.lease_type,
        type: typeof formData.lease_type,
        length: formData.lease_type?.length
      });
      
      // 1. First update the sublease record
      const { data, error } = await supabase
        .from('subleases')
        .update(updateData)
        .match({ lease_id: formData.lease_id })
        .select();

      if (error) {
        console.error('Supabase update error details:', error);
        console.error('Field that might be causing issues - lease_type:', formData.lease_type, 
                    'type:', typeof formData.lease_type, 
                    'length:', formData.lease_type?.length);
        throw error;
      }

      console.log('Sublease update successful, response:', data);

      // 2. Use initiallyAssociatedLotIds (already fetched) to determine what to add/remove
      // No need to fetch currentLeaseLots again.
      
      // Identify lots to add and remove
      const lotsToAdd = selectedLots.filter(lotId => !initiallyAssociatedLotIds.includes(lotId));
      const lotsToRemove = initiallyAssociatedLotIds.filter(lotId => !selectedLots.includes(lotId));

      console.log('Lots to add:', lotsToAdd);
      console.log('Lots to remove:', lotsToRemove);

      // Get Supabase connection details for direct API calls
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Missing Supabase environment variables');
      }

      // 3. Remove lots that were deselected
      if (lotsToRemove.length > 0) {
        const { error: removeError } = await supabase
          .from('lease_lots')
          .delete()
          .eq('leaselot_lease', formData.lease_id)
          .in('leaselot_lot', lotsToRemove);

        if (removeError) {
          console.error('Error removing lease lots:', removeError);
          throw new Error(`Failed to remove deselected lots: ${removeError.message}`);
        }
        
        console.log(`Successfully removed ${lotsToRemove.length} lots`);
      }

      // 4. Add newly selected lots
      if (lotsToAdd.length > 0) {
        const leaseLotsPromises = lotsToAdd.map(lotId => {
          const leaseLotData = {
            leaselot_lease: parseInt(formData.lease_id), // Ensure lease_id is number if required by DB
            leaselot_lot: lotId,
            leaselot_use: leaseLotUse || null // Use null if empty
          };

          const leaseLotPromise = fetch(
            `${supabaseUrl}/rest/v1/lease_lots`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': supabaseKey,
                'Authorization': `Bearer ${supabaseKey}`,
                'Prefer': 'return=representation'
              },
              body: JSON.stringify(leaseLotData)
            }
          );
          return Promise.race([leaseLotPromise, timeoutPromise]) as Promise<Response>;
        });

        // Execute all lease_lots creation requests
        const leaseLotsResponses = await Promise.allSettled(leaseLotsPromises);

        // Check for any failed lease_lots creations
        const failedLeaseLots = leaseLotsResponses.filter(
          response => response.status === 'rejected' || (response.status === 'fulfilled' && !response.value.ok)
        );

        if (failedLeaseLots.length > 0) {
          console.warn(`${failedLeaseLots.length} out of ${lotsToAdd.length} lease_lots creations failed`);
          // Optionally, collect error messages from failed requests
          const errorMessages = await Promise.all(failedLeaseLots.map(async (failure) => {
              if (failure.status === 'rejected') return failure.reason.message;
              if (failure.status === 'fulfilled') return `HTTP ${failure.value.status}: ${await failure.value.text()}`;
              return 'Unknown error';
          }));
          setError(`Sublease updated, but failed to associate ${failedLeaseLots.length} lots. Errors: ${errorMessages.join(', ')}`);
          // We'll continue and return success, but with an error message
        } else {
          console.log(`Successfully added ${lotsToAdd.length} new lots`);
        }
      }

      // 5. Update lease_lot_use for existing associations (if any lots remained selected)
      // These are lots that were initially associated and are still in selectedLots.
      const lotsToUpdate = selectedLots.filter(lotId => initiallyAssociatedLotIds.includes(lotId));
      
      if (lotsToUpdate.length > 0) {
        const { error: updateLotUseError } = await supabase
          .from('lease_lots')
          .update({ leaselot_use: leaseLotUse || null })
          .eq('leaselot_lease', formData.lease_id)
          .in('leaselot_lot', lotsToUpdate);

        if (updateLotUseError) {
          console.error('Error updating lease lot use:', updateLotUseError);
          // Log but don't throw to allow the overall operation to succeed
          console.warn(`Failed to update lease lot use: ${updateLotUseError.message}`);
        } else {
          console.log(`Successfully updated lease lot use for ${lotsToUpdate.length} existing lots`);
        }
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating sublease:', err);
      // Provide a more descriptive error message for debugging
      let errorMessage = 'Failed to update sublease';
      
      if (err instanceof Error) {
        errorMessage += `: ${err.message}`;
      } else if (typeof err === 'object' && err !== null) {
        // Try to extract more meaningful error information from Supabase error object
        try {
          errorMessage += `: ${JSON.stringify(err)}`;
        } catch {
          errorMessage += `: ${String(err)}`;
        }
      } else {
        errorMessage += `: ${String(err)}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalContentRef} className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Edit Sub-Lease</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lease ID (read-only) */}
              <div>
                <label htmlFor="lease_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease ID
                </label>
                <input
                  type="text"
                  id="lease_id"
                  name="lease_id"
                  value={formData.lease_id}
                  readOnly
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Leaseholder */}
              <div>
                <label htmlFor="lease_holder" className="block text-sm font-medium text-gray-700 mb-1">
                  Leaseholder
                </label>
                <select
                  id="lease_holder"
                  name="lease_holder"
                  value={formData.lease_holder}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Select a leaseholder</option>
                  {leaseholders.map(leaseholder => (
                    <option key={leaseholder.id} value={leaseholder.id}>
                      {leaseholder.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Start */}
              <div>
                <label htmlFor="lease_start" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Start Date
                </label>
                <input
                  type="date"
                  id="lease_start"
                  name="lease_start"
                  value={formatDateForInput(formData.lease_start)}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Lease End */}
              <div>
                <label htmlFor="lease_end" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease End Date
                </label>
                <input
                  type="date"
                  id="lease_end"
                  name="lease_end"
                  value={formatDateForInput(formData.lease_end)}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Lease Method */}
              <div>
                <label htmlFor="lease_method" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Method
                </label>
                <select
                  id="lease_method"
                  name="lease_method"
                  value={formData.lease_method === null ? '' : formData.lease_method}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : e.target.value;
                    setFormData(prev => ({ ...prev, lease_method: value }));
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  {leaseMethodOptions.map(option => (
                    <option key={option.code || 'none'} value={option.code || ''}>
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Type */}
              <div>
                <label htmlFor="lease_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Type
                </label>
                <input
                  type="text"
                  id="lease_type"
                  name="lease_type"
                  value={formData.lease_type || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Further Term */}
              <div>
                <label htmlFor="lease_furth_term" className="block text-sm font-medium text-gray-700 mb-1">
                  Further Term
                </label>
                <input
                  type="text"
                  id="lease_furth_term"
                  name="lease_furth_term"
                  value={formData.lease_furth_term || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Rental Frequency */}
              <div>
                <label htmlFor="lease_rent_freq" className="block text-sm font-medium text-gray-700 mb-1">
                  Rental Frequency
                </label>
                <select
                  id="lease_rent_freq"
                  name="lease_rent_freq"
                  value={formData.lease_rent_freq || rentalFrequencyOptions[0]}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  {rentalFrequencyOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Dealing No. */}
              <div>
                <label htmlFor="lease_dealing" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Dealing No.
                </label>
                <input
                  type="text"
                  id="lease_dealing"
                  name="lease_dealing"
                  value={formData.lease_dealing || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Lease Docs */}
            <div>
              <label htmlFor="lease_deal_doc" className="block text-sm font-medium text-gray-700 mb-1">
                Lease Documents
              </label>
              <textarea
                id="lease_deal_doc"
                name="lease_deal_doc"
                value={formData.lease_deal_doc || ''}
                onChange={handleChange}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Lot Selection Section */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Associate Lots</h3>

              {/* Lease Lot Use field */}
              <div className="mb-4">
                <label htmlFor="leaseLotUse" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Lot Use
                </label>
                <input
                  type="text"
                  id="leaseLotUse"
                  name="leaseLotUse"
                  value={leaseLotUse}
                  onChange={handleLeaseLotUseChange}
                  placeholder="Specify the permitted use for selected lots"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  maxLength={200}
                />
                <p className="mt-1 text-sm text-gray-500">
                  This use will be applied to all selected lots. Max 200 characters.
                </p>
              </div>

              {/* Lot Selection Logic */}
              {(() => {
                // Lots to display in "Currently Associated"
                const currentlyAssociatedDisplayLots = currentAssociationDetails;
                
                // Lots to display in "Available to Associate" (selectable pool, excluding those already in currentAssociationDetails)
                const availableToAssociateDisplayLots = selectableLotPool.filter(
                  poolLot => !initiallyAssociatedLotIds.includes(poolLot.lot_id)
                );

                return (
                  <>
                    {/* Currently Associated Lots Section */}
                    {currentAssociationDetails.length > 0 && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currently Associated Lots ({currentAssociationDetails.filter(lot => selectedLots.includes(lot.lot_id)).length} selected)
                        </label>
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                          {currentAssociationDetails.map(lot => (
                            <div key={`current-${lot.lot_id}`} className="flex items-start p-2 hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                id={`current-lot-${lot.lot_id}`}
                                checked={selectedLots.includes(lot.lot_id)}
                                onChange={() => handleLotCheckboxChange(lot.lot_id)}
                                className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              />
                              <label htmlFor={`current-lot-${lot.lot_id}`} className="ml-2 block text-sm cursor-pointer">
                                <span className="font-medium">{lot.lot_id}</span>
                                {lot.community_name && <span className="text-gray-500 ml-2">- {lot.community_name}</span>}
                                {lot.lot_desc && lot.lot_desc !== 'No description' && (
                                  <p className="text-xs text-gray-500 mt-0.5">{lot.lot_desc}</p>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Lots to Associate Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Lots to Associate ({availableToAssociateDisplayLots.filter(lot => selectedLots.includes(lot.lot_id)).length} selected from available pool)
                      </label>
                      {selectableLotPool.length === 0 ? (
                        <div className="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-md">
                          Loading available lots or none found...
                        </div>
                      ) : availableToAssociateDisplayLots.length === 0 ? (
                        <div className="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-md">
                          No other lots available to associate (or all available lots are already associated).
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                          {availableToAssociateDisplayLots.map(lot => (
                            <div key={`available-${lot.lot_id}`} className="flex items-start p-2 hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                id={`available-lot-${lot.lot_id}`}
                                checked={selectedLots.includes(lot.lot_id)}
                                onChange={() => handleLotCheckboxChange(lot.lot_id)}
                                className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              />
                              <label htmlFor={`available-lot-${lot.lot_id}`} className="ml-2 block text-sm cursor-pointer">
                                <span className="font-medium">{lot.lot_id}</span>
                                {lot.community_name && <span className="text-gray-500 ml-2">- {lot.community_name}</span>}
                                {lot.lot_desc && lot.lot_desc !== 'No description' && (
                                  <p className="text-xs text-gray-500 mt-0.5">{lot.lot_desc}</p>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Total Selected Count */}
                    {selectedLots.length > 0 && (
                      <p className="mt-4 text-sm text-gray-600 font-semibold">
                        Total: {selectedLots.length} {selectedLots.length === 1 ? 'lot' : 'lots'} selected
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Submission Buttons */}
            <div className="flex justify-end space-x-3 mt-8 border-t pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
